import { useId } from 'react';
import { FieldGuidanceGroup } from '../../guidance/FieldGuidance';
import { GuidedField } from '../../guidance/GuidedField';
import { fieldDef } from '../../guidance/roleFieldRegistry';
import { formatBandRange, inputFromPayBandForm, emptyPayBandForm, formFromBand, type PayBandForm as PayBandFormValues } from '../../payBand';
import {
  COMPENSATION_DISCLAIMER, EMPLOYER_CLASSIFICATION_WARNING, HOURLY_OR_SALARIED_PRESETS, TYPICAL_EMPLOYMENT_TYPE_PRESETS, TYPICAL_PAY_TYPE_PRESETS,
  type PayBand,
} from '../../types';
import { BenchmarkPanel } from '../BenchmarkPanel';
import { GroupedCombobox } from '../guided/GroupedCombobox';
import { referenceLoader } from '../guided/referenceOptions';
import { PayBandForm } from '../PayBandForm';
import { ChoiceField, ReferenceSelectField, TextAreaField } from './fields';
import { useWizard } from './WizardContext';

const TRI = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] as const;
const asChoices = (values: readonly string[]) => values.map((v) => ({ value: v, label: v }));

export interface PendingBand { values: PayBandFormValues; expected: number | undefined }

export interface CompensationStepProps {
  existingBand: PayBand | null | undefined;
  pendingBand: PendingBand | null;
  bandFormOpen: boolean;
  setBandFormOpen: (open: boolean) => void;
  onKeepBand: (values: PayBandFormValues) => void;
  onRemoveBand: () => void;
  knownRoleId: string | null;
  benchRefresh: number;
  busy: boolean;
  onSaveForBenchmarks: () => void;
}

export const EMPLOYMENT_GUIDANCE_NOTE = 'Guidance only. A role never sets anyone’s pay rate, and never changes what anyone can do in EPN.';
export const COMPENSATION_SOURCE_LIMITATIONS =
  'Market benchmark figures are survey reference data, not the lowest or highest salary anyone is paid, and they do not replace minimum wage, overtime, equal pay or pay transparency rules. Your employer pay band is your own range, a guide and not an employee’s pay. An employee’s actual pay is set elsewhere and is never shown here.';

/**
 * Step 5, Compensation (optional, skippable as a whole): the employment guidance (typical employment and pay type, hourly or salaried, the currency,
 * bonus, commission and overtime eligibility, the employer's FLSA classification and benefit eligibility), the employer pay band, and the market
 * benchmark. The three kinds of pay information stay visibly separate: the market benchmark is reference data, the pay band is the employer's own policy
 * and an employee's actual pay is not here. Whether Set / Edit and the benchmark are OFFERED follows the server's capability flags; a 403 is still handled.
 */
export function CompensationStep(p: CompensationStepProps) {
  const w = useWizard();
  const uid = useId();
  const benefitId = `${uid}-benefit-add`;
  const mayManageBand = w.caps ? w.caps.canManagePayBand : true;
  const mayViewBenchmarks = w.caps ? w.caps.canViewBenchmarks : true;
  const currency = fieldDef('currency');

  return (
    <div className="jr-step" data-testid="wizard-compensation-step">
      <p className="muted">
        Optional. You can skip this whole step: a role can always be saved and activated without a pay band or a benchmark, and you can
        add either later from the role&apos;s Compensation tab.
      </p>
      <p className="jr-notice" role="note">{COMPENSATION_DISCLAIMER}</p>

      <section className="jr-card" aria-labelledby={`${uid}-guidance-title`}>
        <h5 id={`${uid}-guidance-title`} className="jr-card__title">Employment guidance</h5>
        <p className="jr-hint">{EMPLOYMENT_GUIDANCE_NOTE}</p>
        <p className="jr-warning" role="note"><span aria-hidden="true">{'⚠'} </span>{EMPLOYER_CLASSIFICATION_WARNING}</p>
        <div className="jr-grid">
          <ChoiceField k="typicalEmploymentType" choices={asChoices(TYPICAL_EMPLOYMENT_TYPE_PRESETS)} />
          <ChoiceField k="typicalPayType" choices={asChoices(TYPICAL_PAY_TYPE_PRESETS)} />
          <ChoiceField k="hourlyOrSalaried" choices={asChoices(HOURLY_OR_SALARIED_PRESETS)} />
          <GuidedField field={currency}>
            {(a) => <input {...a} type="text" value="USD (US dollars)" readOnly aria-readonly="true" />}
          </GuidedField>
          <ReferenceSelectField k="flsaClassification" />
          <ChoiceField k="overtimeEligible" choices={TRI} />
          <ChoiceField k="commissionEligible" choices={TRI} />
          <ChoiceField k="bonusEligible" choices={TRI} />
          <TextAreaField k="benefitEligibilityGuidance" rows={3} isWide>
            <div className="jr-field jr-benefit-add">
              <label htmlFor={benefitId}>Add a standard statement</label>
              <GroupedCombobox
                a11y={{ id: benefitId, 'aria-describedby': undefined, 'aria-invalid': undefined, 'aria-required': undefined, 'data-field': 'benefitEligibilityStatement' }}
                mode="select" value="" valueLabel="" disabled={w.disabled}
                load={referenceLoader('BenefitEligibility')}
                onSelect={(o) => {
                  const current = w.form.benefitEligibilityGuidance;
                  if (current.split(/\r?\n/).some((l) => l.trim().toLowerCase() === o.label.toLowerCase())) return;
                  w.set('benefitEligibilityGuidance', current.trim() ? `${current.replace(/\s+$/, '')}\n${o.label}` : o.label);
                }}
                placeholder="Search the standard statements"
              />
              <p className="jr-hint">A statement you choose is added on a new line. It never replaces what you wrote.</p>
            </div>
          </TextAreaField>
        </div>
      </section>

      <section className="jr-card" aria-labelledby={`${uid}-band-title`}>
        <h5 id={`${uid}-band-title`} className="jr-card__title">Employer pay band</h5>
        <p className="muted">Your company&apos;s own pay range for this role: a guide, not an employee&apos;s pay. It never changes anyone&apos;s pay.</p>
        {p.existingBand && !p.pendingBand && (
          <p data-testid="wizard-existing-band">Current band: <strong>{formatBandRange(p.existingBand)}</strong></p>
        )}
        {p.pendingBand && !p.bandFormOpen && (
          <div data-testid="wizard-pending-band">
            <p role="status">
              <span aria-hidden="true">{'✓'} </span>
              To be saved with this role: <strong>{formatBandRange(inputFromPayBandForm(p.pendingBand.values))}</strong>
            </p>
            <div className="jr-actions">
              <button type="button" className="jr-btn jr-btn--secondary" onClick={() => p.setBandFormOpen(true)}>Change this pay band</button>
              <button type="button" className="jr-btn jr-btn--secondary" onClick={p.onRemoveBand}>Remove this pay band</button>
            </div>
          </div>
        )}
        {p.bandFormOpen && mayManageBand && (
          <PayBandForm
            initial={p.pendingBand ? { ...p.pendingBand.values, reviewed: false } : p.existingBand ? formFromBand(p.existingBand) : emptyPayBandForm()}
            title={p.existingBand ? 'Change the employer pay band' : 'Add an employer pay band'}
            submitLabel="Keep this pay band"
            focusOnMount
            onSubmit={p.onKeepBand}
            onCancel={() => p.setBandFormOpen(false)}
          >
            <p className="jr-hint">The pay band is saved with the role when you choose Save as draft or Activate. Nothing is saved yet.</p>
          </PayBandForm>
        )}
        {!p.pendingBand && !p.bandFormOpen && mayManageBand && (
          <div className="jr-actions">
            <button type="button" className="jr-btn jr-btn--secondary" onClick={() => p.setBandFormOpen(true)}>
              {p.existingBand ? 'Change the pay band' : 'Add a pay band'}
            </button>
          </div>
        )}
        {!mayManageBand && (
          <p className="jr-readonly" role="note" data-testid="wizard-band-readonly">
            <span aria-hidden="true">{'🔒'} </span>
            You can view a pay band but not set or change one. That needs a separate permission. Ask a company administrator if you need it. The role is saved without a band.
          </p>
        )}
        {mayManageBand && <p className="jr-hint">Setting a pay band needs its own permission. If you do not have it, the role is still saved and you will be told the band was not set.</p>}
      </section>

      <section className="jr-card" aria-labelledby={`${uid}-bench-title`}>
        <div className="jr-fieldhead">
          <h5 id={`${uid}-bench-title`} className="jr-card__title">Market benchmark</h5>
          <FieldGuidanceGroup name="market low, median and high" fields={[fieldDef('marketLow'), fieldDef('marketMedian'), fieldDef('marketHigh')]} />
        </div>
        {p.knownRoleId ? (
          <BenchmarkPanel roleId={p.knownRoleId} refreshKey={p.benchRefresh} allowed={mayViewBenchmarks ? undefined : false} />
        ) : !mayViewBenchmarks ? (
          <BenchmarkPanel roleId="" allowed={false} />
        ) : (
          <>
            <p className="muted">
              Benchmarks are looked up by the role&apos;s SOC code, so the role has to be saved first. Save it as a draft to look them up
              here, or skip this and look later in the role&apos;s Compensation tab.
            </p>
            <div className="jr-actions">
              <button type="button" className="jr-btn jr-btn--secondary" onClick={p.onSaveForBenchmarks} disabled={p.busy}>
                {p.busy ? 'Saving…' : 'Save draft and look up benchmarks'}
              </button>
            </div>
          </>
        )}
        <p className="jr-hint" data-testid="compensation-limitations">{COMPENSATION_SOURCE_LIMITATIONS}</p>
      </section>
    </div>
  );
}
